import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "reactstrap";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import AdminRequestAccountingCreate from "../Models/Request/AdminRequestAccountingCreate";
import AdminRequestAccountingUpdate from "../Models/Request/AdminRequestAccountingUpdate";

/**
 * AccountingFormPanel
 *
 * Centered SlidePanel to create or edit an accounting account, using the same UI
 * as the "new processing" panel in ReportProceedingList (SelectOrgPanel).
 *
 * Props:
 *   isOpen        boolean
 *   accountingId  string | null   null = create, otherwise edit that account
 *   onClose       () => void
 *   onSaved       () => void      called after a successful create/update/reactivation
 */
export function AccountingFormPanel({ isOpen, accountingId = null, onClose, onSaved }) {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();

    const isEdit = !!accountingId;
    const Model = isEdit ? AdminRequestAccountingUpdate : AdminRequestAccountingCreate;
    const documentationOptions = Model.getDocumentationOptions();

    const [accounting, setAccounting] = useState(new Model());
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // ApiCall reports failures via the error callback (after calling the response callback with null)
    const callApi = async (options) => {
        let data = null;
        let error = null;
        await ApiCall(options, (response) => { data = response; }, (message) => { error = message; });
        if (error !== null) throw new Error(error);
        return data;
    };

    // Load on open (edit), reset on close
    useEffect(() => {
        if (!isOpen) {
            setAccounting(new Model());
            setFieldErrors({});
            setSaving(false);
            return;
        }
        if (!isEdit) return;

        let cancelled = false;
        setLoading(true);
        callApi({ method: "get", url: `/accounting?id=${accountingId}` })
            .then((response) => { if (!cancelled) setAccounting(new AdminRequestAccountingUpdate(response)); })
            .catch(() => { if (!cancelled) FlashMessage(t('validationMessage.sendError'), 'flash-messageError'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, accountingId]);

    const clearError = (name) => setFieldErrors(prev => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
    });

    const handleChange = (name, value) => {
        setAccounting(prev => ({ ...prev, [name]: value }));
        clearError(name);
    };

    const handleDocumentationChange = (docValue, isChecked) => {
        setAccounting(prev => {
            const currentDocs = prev.requiredDocumentation || [];
            return {
                ...prev,
                requiredDocumentation: isChecked
                    ? Array.from(new Set([...currentDocs, docValue]))
                    : currentDocs.filter(doc => doc !== docValue),
            };
        });
    };

    // Check duplicates via list search, excluding the current record (edit only)
    const checkExists = async (field, value) => {
        if (!isEdit || !value) return false;
        const trimmed = value.trim().toLowerCase();
        try {
            const response = await callApi({ method: "get", url: `/accounting/list?query=${encodeURIComponent(value)}` });
            const items = (response && (response.items || response)) || [];
            if (!Array.isArray(items)) return false;
            return items.some(it => {
                if (!it || it.id === accountingId) return false;
                const compare = field === 'name' ? (it.name || '') : (it.value || '');
                return compare.trim().toLowerCase() === trimmed;
            });
        } catch (e) {
            console.warn("checkExists failed", e);
            return false;
        }
    };

    const handleBlur = async (field) => {
        const schema = Model.getAttributes(null, true);
        try {
            await schema.validateAt(field, accounting);
        } catch (err) {
            setFieldErrors(prev => ({ ...prev, [field]: err.message }));
            return;
        }
        if (await checkExists(field, accounting[field])) {
            setFieldErrors(prev => ({ ...prev, [field]: field === 'name' ? 'validationMessage.nameExists' : 'validationMessage.accountExists' }));
        }
    };

    // The API can answer 200 with a "validation.duplicate..." string
    const handleDuplicateResponse = (response) => {
        if (!(typeof response === 'string' && response.includes('validation.duplicate'))) return false;

        if (response.includes('validation.duplicateValue')) {
            FlashMessage(t('validationMessage.accountExists'), 'flash-messageError');
            setFieldErrors(prev => ({ ...prev, value: 'validationMessage.accountExists' }));
        } else if (response.includes('validation.duplicateName')) {
            FlashMessage(t('validationMessage.nameExists'), 'flash-messageError');
            setFieldErrors(prev => ({ ...prev, name: 'validationMessage.nameExists' }));
        } else if (response.includes('validation.duplicateEntry')) {
            FlashMessage(t('validationMessage.duplicateEntry'), 'flash-messageError');
            setFieldErrors(prev => ({ ...prev, name: 'validationMessage.duplicateEntry', value: 'validationMessage.duplicateEntry' }));
        } else {
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        }
        return true;
    };

    const checkRequired = () =>
        !!accounting.value && accounting.value.trim() !== "" &&
        !!accounting.name && accounting.name.trim() !== "";

    const handleSave = async () => {
        if (saving || !checkRequired()) return;

        const schema = Model.getAttributes(null, true);
        try {
            await schema.validate(accounting, { abortEarly: false });
        } catch (err) {
            const newErrors = {};
            (err?.inner?.length ? err.inner : [err]).forEach(e => {
                if (e?.path) newErrors[e.path] = e.message;
            });
            setFieldErrors(newErrors);
            return;
        }

        setSaving(true);

        if (isEdit) {
            const [nameExists, valueExists] = await Promise.all([
                checkExists('name', accounting.name),
                checkExists('value', accounting.value),
            ]);
            if (nameExists || valueExists) {
                setFieldErrors(prev => ({
                    ...prev,
                    ...(nameExists ? { name: 'validationMessage.nameExists' } : {}),
                    ...(valueExists ? { value: 'validationMessage.accountExists' } : {}),
                }));
                setSaving(false);
                return;
            }
        }

        let url = isEdit
            ? `/accounting/update?id=${accountingId}&`
            : `/accounting/create?`;
        url += `name=${encodeURIComponent(accounting.name ?? "")}&value=${encodeURIComponent(accounting.value ?? "")}&message=${encodeURIComponent(accounting.message ?? "")}`;
        (accounting.requiredDocumentation || []).forEach((doc, index) => {
            url += `&requiredDocumentation[${index}]=${encodeURIComponent(doc)}`;
        });

        try {
            const response = await callApi({ method: isEdit ? "put" : "post", url });
            if (handleDuplicateResponse(response) || !response) {
                setSaving(false);
                return;
            }
            FlashMessage(t(isEdit ? 'validationMessage.accountingUpdate' : 'validationMessage.accountingSuccessfully'), 'flash-messageSuccess');
            onClose();
            if (onSaved) onSaved();
        } catch (error) {
            const message = error?.message || '';
            if (message.includes('validationMessage.accountExists') || message.includes('validationMessage.nameExists')) {
                FlashMessage(t(message), 'flash-messageError');
            } else {
                FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            }
            setSaving(false);
        }
    };

    const handleReactivate = async () => {
        setSaving(true);
        try {
            const response = await callApi({ method: "put", url: `/accounting/reactivate?id=${accountingId}` });
            if (response) setAccounting(new AdminRequestAccountingUpdate(response));
            FlashMessage(t('validationMessage.accountingReactivated'), 'flash-messageSuccess');
            if (onSaved) onSaved();
        } catch (error) {
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        } finally {
            setSaving(false);
        }
    };

    const renderFieldError = (name) => {
        const err = fieldErrors[name];
        if (!err) return null;
        return (
            <div className="mt-1" style={{ color: '#f56e6e', fontSize: '12px' }}>
                {typeof err === 'string' ? t(err) : err}
            </div>
        );
    };

    const canSave = checkRequired() && !saving && Object.keys(fieldErrors).length === 0;

    return (
        <SlidePanel
            isOpen={isOpen}
            onClose={onClose}
            title={t(isEdit ? 'accounting.update' : 'accounting.create')}
            variant="center"
        >
            <div className="select-org-panel-wrapper">
                {loading ? (
                    <div className="select-org-panel-loading">
                        <Spinner color="success" size="sm" />
                        <span>{t("sortableList.gettingData")}</span>
                    </div>
                ) : (
                    <>
                        {isEdit && accounting.deleted && (
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 p-3 rounded" style={{ background: '#FCEAEA', color: '#C0392B', fontSize: '13px' }}>
                                <span>{t('validationMessage.modalReactivate')}</span>
                                <button onClick={handleReactivate} disabled={saving} className="select-org-panel-btn-confirm enabled flex-grow-0 px-3 py-2">
                                    {t('button.reactivate')}
                                </button>
                            </div>
                        )}

                        {/* Account number: digits only, max 4 */}
                        <div className="mb-3">
                            <CustomInput
                                model={Model}
                                attribute="value"
                                data={accounting.value}
                                customChange={(e) => handleChange('value', (e?.target?.value ?? "").replace(/\D/g, '').slice(0, 4))}
                                inputProps={{ id: 'value', inputMode: 'numeric', onBlur: () => handleBlur('value') }}
                            />
                            {renderFieldError('value')}
                        </div>

                        {/* Name */}
                        <div className="mb-3">
                            <CustomInput
                                model={Model}
                                attribute="name"
                                data={accounting.name}
                                customChange={(e) => handleChange('name', e?.target?.value ?? "")}
                                inputProps={{ id: 'name', onBlur: () => handleBlur('name') }}
                            />
                            {renderFieldError('name')}
                        </div>

                        {/* Message */}
                        <div className="mb-3">
                            <label htmlFor="accountingMessage" className="select-org-panel-label">
                                {t('accounting.message')}
                            </label>
                            <textarea
                                id="accountingMessage"
                                className="form-control"
                                value={accounting.message || ""}
                                onChange={(e) => handleChange('message', e.target.value)}
                                style={{ resize: "vertical", minHeight: "80px", height: "auto" }}
                                rows={3}
                            />
                            {renderFieldError('message')}
                        </div>

                        {/* Required documentation */}
                        <div className="mb-4">
                            <hr className="mt-0 mb-3" />
                            <p className="select-org-panel-intro mb-1">{t('accounting.requiredDocumentation')}</p>
                            <p className="select-org-panel-label mb-2">{t('accounting.selectRequiredDocs')}</p>
                            {documentationOptions.map((option) => {
                                const inputId = `doc-${option.value.replace(/\s+/g, '-')}`;
                                return (
                                    <div className="rpv2-multiday-row mt-0 mb-2" key={option.value}>
                                        <input
                                            type="checkbox"
                                            id={inputId}
                                            className="rpv2-multiday-checkbox"
                                            checked={(accounting.requiredDocumentation || []).includes(option.value)}
                                            disabled={saving}
                                            onChange={(e) => handleDocumentationChange(option.value, e.target.checked)}
                                        />
                                        <label htmlFor={inputId} className="rpv2-multiday-label">
                                            {t(option.label)}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Buttons */}
                        <div className="select-org-panel-buttons">
                            <button onClick={onClose} className="select-org-panel-btn-cancel">
                                {t('button.cancel') || "Avbryt"}
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={!canSave}
                                className={`select-org-panel-btn-confirm ${canSave ? "enabled" : "disabled"}`}
                            >
                                {saving
                                    ? <Spinner size="sm" />
                                    : t(isEdit ? 'button.update' : 'button.create')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </SlidePanel>
    );
}
